'use client';

import { Children, cloneElement, isValidElement, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import type { Locale } from '@/lib/localePath';

export type InternationalTeamSectionTabId = string;

type TabConfig = {
  id: InternationalTeamSectionTabId;
  label: string;
};

type PanelProps = {
  'data-tab'?: InternationalTeamSectionTabId;
  children?: ReactNode;
};

type Props = {
  locale: Locale;
  tabs: TabConfig[];
  children: ReactNode;
  initialTab?: InternationalTeamSectionTabId;
  ariaLabel?: string;
  className?: string;
  sticky?: boolean;
};

export default function InternationalTeamSectionTabs({
  locale,
  tabs,
  children,
  initialTab = 'rank',
  ariaLabel,
  className,
  sticky = true,
}: Props) {
  const panels = useMemo(
    () => Children.toArray(children).filter(isValidElement) as ReactElement<PanelProps, any>[],
    [children],
  );

  const availableTabIds = useMemo(
    () => panels.map((panel) => panel.props['data-tab']).filter((value): value is InternationalTeamSectionTabId => Boolean(value)),
    [panels],
  );

  const preferredTab = availableTabIds.includes(initialTab)
    ? initialTab
    : (availableTabIds[0] ?? tabs[0]?.id ?? initialTab);

  const [activeTab, setActiveTab] = useState<InternationalTeamSectionTabId>(preferredTab);

  useEffect(() => {
    if (!availableTabIds.includes(activeTab)) {
      setActiveTab(preferredTab);
    }
  }, [activeTab, availableTabIds, preferredTab]);

  const activePanel = panels.find((panel) => panel.props['data-tab'] === activeTab) ?? panels[0] ?? null;
  const visibleTabs = tabs.filter((tab) => availableTabIds.includes(tab.id));
  const tabListLabel = ariaLabel
    ?? (locale === 'ja'
      ? 'セクション切り替え'
      : locale === 'es'
        ? 'Cambiar sección'
        : 'Section switcher');

  return (
    <div className={className}>
      <nav
        className={`info-tabs international-tab-row international-team-section-tabs${sticky ? ' is-sticky' : ''}`}
        role="tablist"
        aria-label={tabListLabel}
      >
        {visibleTabs.map((tab) => {
          const active = tab.id === activeTab;
          const tabId = `international-team-tab-${tab.id}`;
          const panelId = `international-team-panel-${tab.id}`;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={tabId}
              className={`info-tab-button ${active ? 'active' : ''}`}
              aria-selected={active}
              aria-controls={panelId}
              tabIndex={active ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      {activePanel && isValidElement(activePanel)
        ? cloneElement(activePanel as ReactElement<any>, {
          id: `international-team-panel-${activeTab}`,
          role: 'tabpanel',
          'aria-labelledby': `international-team-tab-${activeTab}`,
        })
        : null}
    </div>
  );
}
